import { useEffect, useMemo } from 'react';

import { Button, type MantineColorsTuple, type MantineThemeOverride } from '@mantine/core';
import chroma from 'chroma-js';

import { theme } from 'components/styles/theme';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';

const PRIMARY_BUTTON_COLOR = 'workspacePrimaryButton';

const workspacePalette = (color: string): MantineColorsTuple => {
    const tints = chroma.scale(['#ffffff', color]).mode('lch');
    const shades = chroma.scale([color, '#000000']).mode('lch');
    const tint = (step: number) => tints(step).hex();

    return [
        tint(0.08),
        tint(0.18),
        tint(0.32),
        tint(0.48),
        tint(0.62),
        tint(0.76),
        tint(0.9),
        color,
        shades(0.18).hex(),
        shades(0.34).hex(),
    ];
};

export const useWorkspaceTheme = (): MantineThemeOverride => {
    const { brandColor, primaryButtonColor, gradientFrom, gradientTo, footerColor } = useWorkspaceConfig();

    useEffect(() => {
        const root = document.documentElement;
        const vars: Record<string, string | undefined> = {
            '--workspace-gradient-from': gradientFrom,
            '--workspace-gradient-to': gradientTo,
            '--workspace-footer-color': footerColor,
        };

        Object.entries(vars).forEach(([key, value]) => {
            if (value) {
                root.style.setProperty(key, value);
            }
        });
    }, [gradientFrom, gradientTo, footerColor]);

    return useMemo(() => {
        if (!brandColor && !primaryButtonColor) return theme;

        const colors = { ...theme.colors };
        const components = { ...theme.components };

        if (brandColor) {
            const brandColors = workspacePalette(brandColor);

            colors.brand = brandColors;
            colors.primary = brandColors;
        }

        if (primaryButtonColor) {
            colors[PRIMARY_BUTTON_COLOR] = workspacePalette(primaryButtonColor);

            components.Button = Button.extend({
                ...theme.components?.Button,
                defaultProps: { ...theme.components?.Button?.defaultProps, color: PRIMARY_BUTTON_COLOR },
                vars: (mantineTheme, props) => {
                    const variant = props.variant ?? 'filled';

                    if (props.color !== PRIMARY_BUTTON_COLOR || variant === 'filled' || variant === 'default') {
                        return { root: {} };
                    }

                    const colors = mantineTheme.variantColorResolver({
                        color: 'black',
                        theme: mantineTheme,
                        variant,
                        gradient: props.gradient,
                        autoContrast: props.autoContrast,
                    });

                    return {
                        root: {
                            '--button-bg': colors.background,
                            '--button-hover': colors.hover,
                            '--button-color': colors.color,
                            '--button-bd': colors.border,
                            '--button-hover-color': colors.hoverColor,
                        },
                    };
                },
            });
        }

        return { ...theme, colors, components };
    }, [brandColor, primaryButtonColor]);
};
